import React, { useState, useEffect } from "react";
import "./createChart.scss";
import Dashboard from "../../layout/Dashboard/Dashboard";
import line from "../../assets/images/line.png";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useDatasets } from "../../hooks/useDatasets";
import CookieManager from "../../components/Cookies/CookieManager";
import { useTranslation } from "react-i18next";
import "../../i18n";
import DatasetSelector from "../../components/Dataset/DatasetSelector";
import useSelectionStore from "../../store/useSelectionStore";

const CreateChart = () => {
  const { t } = useTranslation();
  const { selectedDataset, categoriUpdate } = useSelectionStore(); // selectedDataset is the id of selected dataset and categoriUpdated is the selected category
  const [selectedSets, setSelectedSets] = useState(false);
  const [selectedXAxis, setSelectedXaxis] = useState([]);
  const [selectedYAxis, setSelectedYaxis] = useState([]);
  const [datasetColumns, setDatasetColumns] = useState([]);
  const [chartType, setChartType] = useState(null);
  const [chartName, setChartName] = useState("");
  const [data, setData] = useState([]);
  const [warnings, setWarnings] = useState({});
  const navigate = useNavigate();
  const [label, setLabel] = useState(null);
  const [riskFactors, setRiskFactors] = useState([]);
  const [selectedRiskFactor, setSelectedRiskFactor] = useState(null);
  const [loadingRiskFactors, setLoadingRiskFactors] = useState(false);

  const { getSpecificDataset } = useDatasets();
  const geomapcategories = ["Factors Related to Screening", "Air Pollution", "fusion", "CRC Incidence Rates", "Risk Scores"];
  // Categories that should ONLY show GeoMap (no other chart types)
  const geoMapOnlyCategories = ["Risk Scores"];
  const isFusionCategory = categoriUpdate === "fusion";
  const isGeoMapOnlyCategory = geoMapOnlyCategories.includes(categoriUpdate);
  useEffect(() => {
    const fetchAndProcessDataset = async () => {
      if (!selectedDataset) return;
      setSelectedSets(true);
      // Set loading state for fusion category
      if (categoriUpdate === "fusion") {
        setLoadingRiskFactors(true);
      }
      try {
        const data = await getSpecificDataset(selectedDataset);
        setLabel(data.datasetName);
        const uniqueKeys = [
          ...new Set(
            data.elements.flatMap((dataPoint) => Object.keys(dataPoint))
          ),
        ];
        const columns = uniqueKeys.map((key) => ({
          value: key,
          label: key,
        }));

        setDatasetColumns(columns);
        setData(data.elements);

        // For fusion category, extract unique risk factors
        if (categoriUpdate === "fusion") {
          const uniqueRiskFactors = [
            ...new Set(data.elements.map((item) => item["Risk Factor"]).filter(Boolean)),
          ];
          const riskFactorOptions = uniqueRiskFactors.map((rf) => ({
            value: rf,
            label: rf,
          }));
          setRiskFactors(riskFactorOptions);
          setLoadingRiskFactors(false);
          // Auto-select Geographical Chart for fusion
          setChartType({ value: "Geographical Chart", label: "Geographical Chart" });
        }

        // Auto-select Geographical Chart for GeoMap-only categories (e.g., Risk Scores)
        if (geoMapOnlyCategories.includes(categoriUpdate)) {
          setChartType({ value: "Geographical Chart", label: "Geographical Chart" });
        }
      } catch (error) {
        console.error("Error fetching dataset:", error);
        setLoadingRiskFactors(false);
      }
    };

    fetchAndProcessDataset();

    // eslint-disable-next-line
  }, [selectedDataset, categoriUpdate]);

  const handleChartTypeChange = (option) => {
    setChartType(option);
    localStorage.setItem("selectedChartType", JSON.stringify(option));
  };

  const handleCreateChart = async () => {
    let warnings = {};
    if (!selectedSets) {
      warnings.selectedSets = "Please select a dataset.";
    }
    if (!chartType && !isFusionCategory && !isGeoMapOnlyCategory) {
      warnings.chartType = "Please select a chart type.";
    }
    if (
      selectedXAxis.length === 0 &&
      chartType?.value !== "Geographical Chart" &&
      !isFusionCategory
    ) {
      warnings.selectedXAxis = "Please select an X axis.";
    }
    if (
      selectedYAxis.length === 0 &&
      chartType?.value !== "Geographical Chart" &&
      !isFusionCategory
    ) {
      warnings.selectedYAxis = "Please select at least one Y axis.";
    }
    // For fusion category, require risk factor selection
    if (isFusionCategory && !selectedRiskFactor) {
      warnings.selectedRiskFactor = "Please select a Risk Factor.";
    }
    if (!chartName) {
      warnings.chartName = "Please enter a chart name.";
    }

    if (Object.keys(warnings).length > 0) {
      setWarnings(warnings);
      return;
    }

    try {
      const pivotData = {
        name: chartName,
        elements: data,
        config: {
          report: {
            slice: {
              columns: selectedXAxis
                ? [{ uniqueName: selectedXAxis.value }]
                : [],
              rows: selectedYAxis ? [{ uniqueName: selectedYAxis.value }] : [],
            },
          },
        },
      };

      navigate("/generated-chart", {
        state: {
          pivotData,
          label,
          category: categoriUpdate,
          selectedDataset,
          chartType: chartType?.value || "Geographical Chart",
          yKey: selectedXAxis ? selectedXAxis.value : null,
          xKeys: selectedYAxis ? selectedYAxis.value : null,
          chartName,
          // Pass risk factor and all risk factors for fusion category
          selectedRiskFactor: selectedRiskFactor?.value || null,
          riskFactors: riskFactors,
        },
      });
    } catch (error) {
      console.error("Error creating table: ", error.message);
    }
  };

  return (
    <div>
      <Dashboard>
        <CookieManager />
        <h2 className="createChartHeader">{t("createChartHeader")}</h2>
        <p className="createChartDesc">{t("creatChartdescription")}</p>
        <img className="separator" src={line} alt="" />
        <div className="createChartGrid">
          <h4 className="chartHeader">{t("selcectDataset")}</h4>
          <Container>
            <Row>
              <Col>
                <DatasetSelector />
                {warnings.selectedSets && (
                  <p className="warning-text">{warnings.selectedSets}</p>
                )}
              </Col>
            </Row>
          </Container>
          <h4 className="chartHeader">{t("plotInfo")}</h4>
          <Container>
            <Row className="form-row">
              {/* Chart Type - Hidden for fusion category */}
              {!isFusionCategory && (
                <Col className="form-group">
                  <label> {t("chartType")} </label>
                  <Select
                    placeholder="Select Chart Type"
                    options={
                      // GeoMap-only categories (e.g., Risk Scores) - only show Geographical Chart
                      selectedSets && isGeoMapOnlyCategory
                        ? [
                            {
                              value: "Geographical Chart",
                              label: "Geographical Chart",
                            },
                          ]
                        : selectedSets && geomapcategories.includes(categoriUpdate)
                        ? [
                            {
                              value: "Geographical Chart",
                              label: "Geographical Chart",
                            },
                            {
                              value: "Grouped Column Chart",
                              label: "Grouped Column Chart",
                            },
                            {
                              value: "Stacked Column Chart",
                              label: "Stacked Column Chart",
                            },
                            {
                              value: "Grouped Bar Chart",
                              label: "Grouped Bar Chart",
                            },
                            {
                              value: "Stacked Bar Chart",
                              label: "Stacked Bar Chart",
                            },
                            { value: "Line Chart", label: "Line Chart" },
                            { value: "Dot Chart", label: "Dot Chart" },
                            { value: "Area Chart", label: "Area Chart" },
                            {
                              value: "Scatter Chart",
                              label: "Scatter Chart",
                            },
                            {
                              value: "Multiple Pie Chart",
                              label: "Multiple Pie Chart",
                            },
                          ]
                        : [
                            {
                              value: "Grouped Column Chart",
                              label: "Grouped Column Chart",
                            },
                            {
                              value: "Stacked Column Chart",
                              label: "Stacked Column Chart",
                            },
                            {
                              value: "Grouped Bar Chart",
                              label: "Grouped Bar Chart",
                            },
                            {
                              value: "Stacked Bar Chart",
                              label: "Stacked Bar Chart",
                            },
                            { value: "Line Chart", label: "Line Chart" },
                            { value: "Dot Chart", label: "Dot Chart" },
                            { value: "Area Chart", label: "Area Chart" },
                            {
                              value: "Scatter Chart",
                              label: "Scatter Chart",
                            },
                            {
                              value: "Multiple Pie Chart",
                              label: "Multiple Pie Chart",
                            },
                          ]
                    }
                    name="ChartType"
                    className=" dropdown"
                    classNamePrefix="select"
                    onChange={handleChartTypeChange}
                    value={chartType}
                    isDisabled={isGeoMapOnlyCategory}
                  />
                  {warnings.chartType && (
                    <p className="warning-text">{warnings.chartType}</p>
                  )}
                </Col>
              )}

              {/* X Axis - Hidden for fusion category */}
              {!isFusionCategory && (
                <Col className="form-group">
                  <label> {t("xAxis")} </label>
                  <Select
                    placeholder="Select X Axis"
                    options={datasetColumns}
                    className="dropdown"
                    classNamePrefix="select"
                    name="xAxis"
                    value={selectedXAxis}
                    onChange={(options) => setSelectedXaxis(options)}
                    isDisabled={
                      selectedSets &&
                      chartType &&
                      chartType?.value === "Geographical Chart" &&
                      ["Air Pollution", "Factors Related to Screening", "CRC Incidence Rates", "Risk Scores"].includes(
                        categoriUpdate
                      )
                    }
                  />
                  {warnings.selectedXAxis && (
                    <p className="warning-text">{warnings.selectedXAxis}</p>
                  )}
                </Col>
              )}

              {/* Y Axis - Hidden for fusion category */}
              {!isFusionCategory && (
                <Col className="form-group">
                  <label> {t("yAxis")} </label>
                  <Select
                    placeholder="Select Y Axis"
                    options={datasetColumns}
                    className="dropdown"
                    classNamePrefix="select"
                    name="yAxis"
                    value={selectedYAxis}
                    onChange={(option) => setSelectedYaxis(option)}
                    isDisabled={
                      selectedSets &&
                      chartType &&
                      chartType?.value === "Geographical Chart" &&
                      ["Air Pollution", "Factors Related to Screening", "CRC Incidence Rates", "Risk Scores"].includes(
                        categoriUpdate
                      )
                    }
                  />
                  {warnings.selectedYAxis && (
                    <p className="warning-text">{warnings.selectedYAxis}</p>
                  )}
                </Col>
              )}

              {/* Risk Factor dropdown - Only shown for fusion category */}
              {isFusionCategory && selectedSets && (
                <Col className="form-group">
                  <label> {t("riskFactor")} </label>
                  <Select
                    placeholder={loadingRiskFactors ? "Loading Risk Factors..." : "Select Risk Factor"}
                    options={riskFactors}
                    className="dropdown"
                    classNamePrefix="select"
                    name="riskFactor"
                    value={selectedRiskFactor}
                    onChange={(option) => setSelectedRiskFactor(option)}
                    isLoading={loadingRiskFactors}
                    isDisabled={loadingRiskFactors}
                  />
                  {warnings.selectedRiskFactor && (
                    <p className="warning-text">{warnings.selectedRiskFactor}</p>
                  )}
                </Col>
              )}

              <Col className="form-group">
                <label> {t("chartName:")} </label>
                <Form.Control
                  type="text"
                  placeholder="Chart Name"
                  value={chartName}
                  onChange={(e) => setChartName(e.target.value)}
                />
                {warnings.chartName && (
                  <p className="warning-text">{warnings.chartName}</p>
                )}
              </Col>
            </Row>
          </Container>
        </div>
        <Container className="BtnContainerCenter">
          <Button
            className="custom-btn"
            //bsPrefix="nextBtn"
            onClick={handleCreateChart}
          >
            {t("homeCreateChart")}
          </Button>
        </Container>
      </Dashboard>
    </div>
  );
};

export default CreateChart;
